import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'
import CardHeaderTitle from './CardHeaderTitle'
import DeleteButton from './DeleteButton'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Title text to display (optional - prefer using CardHeaderTitle as child for composability)
   * @deprecated Use CardHeaderTitle as a child instead for better composability
   */
  title?: string
  /**
   * Whether to show the delete/remove button (optional - prefer using DeleteButton as child)
   * @deprecated Use DeleteButton as a child instead for better composability
   */
  showDelete?: boolean
  /**
   * Called when delete button is clicked (optional - prefer using DeleteButton as child)
   * @deprecated Use DeleteButton as a child instead for better composability
   */
  onDelete?: () => void
  children?: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Composable CardHeader component.
 *
 * Header area for Card. Can be used in two ways:
 *
 * 1. Composable (recommended):
 * @example
 * <CardHeader>
 *   <CardHeaderTitle>ITEM 1</CardHeaderTitle>
 *   <CardHeaderActions>
 *     <DeleteButton onClick={handleDelete} aria-label="Delete Item 1" />
 *   </CardHeaderActions>
 * </CardHeader>
 *
 * 2. Legacy props-based (deprecated but still supported):
 * @example
 * <CardHeader title="ITEM 1" showDelete onDelete={handleDelete} />
 */
const CardHeader = React.memo(
  ({
    title,
    showDelete = false,
    onDelete,
    children,
    style,
    className,
    ...props
  }: CardHeaderProps) => {
    const [css, theme] = useStyletron()

    const headerClassName = css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      backgroundColor: theme.colors.grey10,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: theme.colors.grey30,
      borderTopLeftRadius: theme.borders.radius200,
      borderTopRightRadius: theme.borders.radius200,
    })

    // Legacy mode: title prop is provided
    const isLegacyMode = title !== undefined

    if (isLegacyMode) {
      // Backwards-compatible rendering with title prop
      const actionsClassName = css({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      })

      return (
        <div
          className={`${headerClassName} ${className || ''}`}
          style={style}
          {...props}
        >
          <CardHeaderTitle>{title}</CardHeaderTitle>
          <div className={actionsClassName}>
            {children}
            {showDelete && (
              <DeleteButton onClick={onDelete} aria-label={`Delete ${title}`} />
            )}
          </div>
        </div>
      )
    }

    // Composable mode: children are the content
    return (
      <div
        className={`${headerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

CardHeader.displayName = 'CardHeader'

export default CardHeader
